import {
  descriptionAffichable, dureeDe, echapper, libelleDomaine, lignes, pluriel,
  type Dossier,
} from './modele.ts';

/**
 * Impression des fiches de préparation.
 *
 * La seule sortie papier du module, et elle doit être présentable : une page
 * de garde par séquence — détail, objectifs, sommaire —, puis une page par
 * séance avec son déroulement. C'est
 * la disposition de la v1 ; les couleurs, elles, sont celles de l'application
 * — le bleu de la marque et ses teintes douces —, prises dans ses jetons de
 * thème pour suivre un changement de charte sans retouche ici.
 *
 * ── Toutes les séances, à la suite ──────────────────────────────────────────
 * L'écran n'en montre qu'une, par onglet. La feuille les garde toutes : on
 * imprime une séquence pour l'avoir entière sous la main.
 */

const liste = (objectifs: { text: string }[]) =>
  `<ul>${objectifs.map((o) => `<li>${echapper(o.text)}</li>`).join('')}</ul>`;

/** Une séquence : sa page de garde, puis une page par séance. */
export function htmlSequence({ sequence: s, sessions }: Dossier): string {
  const garde = `
    <section class="fp-feuille fp-garde">
      <div class="fp-bandeau">
        <span class="fp-etiquette">Fiche de préparation</span>
        <h1>${echapper(s.title)}</h1>
        <p class="fp-domaine">${echapper(libelleDomaine(s))}</p>
      </div>
      ${s.detail ? `<h2>Détail de la séquence</h2><p class="fp-para">${lignes(s.detail)}</p>` : ''}
      ${s.objectives.length ? `<h2>Objectifs de la séquence</h2>${liste(s.objectives)}` : ''}
      ${sessions.length ? `<h2>Sommaire</h2>
        <ol class="fp-sommaire">${sessions.map((se) => `<li>${echapper(se.title || 'Sans titre')}
          <span>${dureeDe(se.phases)} min</span></li>`).join('')}</ol>` : ''}
    </section>`;

  const pages = sessions.map((se, i) => {
    const total = dureeDe(se.phases);
    return `
    <section class="fp-feuille">
      <header class="fp-tete-seance">
        <div><span class="fp-num">Séance ${i + 1}</span><h1>${echapper(se.title || 'Sans titre')}</h1></div>
        <div class="fp-rappel">${echapper(s.title)}${total ? ` · ${total} min` : ''}</div>
      </header>
      ${se.objectives.length ? `<h2>Objectifs</h2>${liste(se.objectives)}` : ''}
      ${se.material || se.comment || se.attachments.length ? `<div class="fp-deux-colonnes">
        ${se.material ? `<div><h2>Matériel</h2><p class="fp-para">${lignes(se.material)}</p></div>` : ''}
        ${se.comment ? `<div><h2>Commentaire</h2><p class="fp-para">${lignes(se.comment)}</p></div>` : ''}
        ${se.attachments.length ? `<div><h2>Pièces jointes</h2>
          <ul class="fp-pieces">${se.attachments.map((a) => `<li>${echapper(a.name)}</li>`).join('')}</ul></div>` : ''}
      </div>` : ''}
      ${se.phases.length ? `<h2>Déroulement</h2>
        ${se.phases.map((p, k) => `
          <div class="fp-phase">
            <table class="fp-phase-tete"><tr>
              <td class="fp-phase-num">${k + 1}</td>
              <td class="fp-champ"><span>Titre de la phase</span><strong>${echapper(p.title)}</strong></td>
              <td class="fp-champ fp-etroit"><span>Durée</span><strong>${p.duration ? `${p.duration} min` : '-'}</strong></td>
              <td class="fp-champ"><span>Type de phase</span><strong>${echapper(p.kind || '-')}</strong></td>
              <td class="fp-champ"><span>Dispositif</span><strong>${echapper(p.grouping || '-')}</strong></td>
            </tr></table>
            <div class="fp-phase-desc">${descriptionAffichable(p.description) || '<span class="fp-vide">-</span>'}</div>
          </div>`).join('')}`
        : '<p class="fp-para fp-vide">Aucune phase renseignée.</p>'}
    </section>`;
  }).join('');

  return garde + pages;
}

/**
 * La couche propre aux fiches, posée sur celle de toutes les impressions.
 *
 * Les couleurs sont des JETONS de l'application : le document emporte ses
 * feuilles de style, en thème clair.
 */
export const STYLE_FICHES = `
  body { font-size: 9.5pt; color: var(--color-fg); }
  .fp-feuille { break-after: page; }
  .fp-feuille:last-child { break-after: auto; }
  .fp-feuille h1, .fp-feuille h2 { border: none; }

  .fp-bandeau { background: var(--color-primary-soft); border: 1.5px solid var(--color-primary);
                border-radius: 5mm; padding: 10mm 8mm; text-align: center; margin-bottom: 7mm; }
  .fp-etiquette { display: inline-block; background: var(--color-primary); color: var(--color-primary-fg);
                  border-radius: 2.5mm; padding: 1.5mm 5mm; font-size: 8pt; font-weight: 600;
                  letter-spacing: .3mm; text-transform: uppercase; margin-bottom: 4mm; }
  .fp-garde h1 { font-size: 22pt; margin: 0 0 3mm; color: var(--color-primary-soft-fg); }
  .fp-domaine { font-size: 11pt; color: var(--color-fg-muted); }

  .fp-feuille h2 { font-size: 10pt; font-weight: 700; color: var(--color-primary);
                   margin: 6mm 0 2.5mm; padding-bottom: 1.5mm;
                   border-bottom: 1px solid var(--color-border); }
  .fp-para { line-height: 1.5; }
  .fp-vide { color: var(--color-fg-subtle); font-style: italic; }
  .fp-feuille ul, .fp-feuille ol { margin: 0 0 0 5mm; padding: 0; }
  .fp-feuille ul { list-style: disc; }
  .fp-feuille ol { list-style: decimal; }
  .fp-feuille li { margin-bottom: 1.5mm; line-height: 1.4; }
  .fp-sommaire li span { color: var(--color-fg-muted); font-size: 8.5pt; margin-left: 2mm; }

  .fp-tete-seance { display: flex; justify-content: space-between; align-items: flex-end; gap: 6mm;
                    border-bottom: 2px solid var(--color-primary); padding-bottom: 3mm; margin-bottom: 2mm; }
  .fp-num { display: inline-block; background: var(--color-primary); color: var(--color-primary-fg);
            border-radius: 2mm; padding: 1mm 3mm; font-size: 7.5pt; font-weight: 600; margin-bottom: 1.5mm; }
  .fp-tete-seance h1 { font-size: 15pt; margin: 0; }
  .fp-rappel { font-size: 8pt; color: var(--color-fg-muted); text-align: right; }

  /* Matériel, commentaire et pièces jointes partagent la bande sous le titre :
     ce sont les trois choses à préparer avant d'entrer en classe. Un texte
     écrit d'un seul tenant débordait sur la colonne voisine : les mots cèdent
     avant la colonne. */
  .fp-deux-colonnes { display: flex; flex-wrap: wrap; align-items: flex-start; gap: 6mm; }
  .fp-deux-colonnes > div { flex: 1 1 45mm; min-width: 0; overflow-wrap: anywhere; }

  /* Une phase : un bandeau de champs, la description en dessous sur toute la
     largeur. La disposition de l'écran, pour qu'on retrouve sa fiche telle
     qu'on l'a saisie. */
  .fp-phase { border: 1px solid var(--color-border); border-radius: 2mm; margin-bottom: 3mm;
              overflow: hidden; break-inside: avoid; }
  table.fp-phase-tete { width: 100%; table-layout: fixed; border-collapse: collapse;
                        background: var(--color-primary-soft); }
  table.fp-phase-tete td { padding: 1.8mm 2.5mm; vertical-align: top;
                           border-right: 1px solid var(--color-border); overflow-wrap: anywhere; }
  table.fp-phase-tete td:last-child { border-right: none; }
  td.fp-phase-num { width: 7mm; text-align: center; vertical-align: middle !important;
                    color: var(--color-primary-fg); background: var(--color-primary);
                    font-weight: 700; font-size: 10pt; }
  td.fp-etroit { width: 17mm; }
  .fp-champ span { display: block; font-size: 6.5pt; font-weight: 600; color: var(--color-primary-soft-fg);
                   text-transform: uppercase; letter-spacing: .2mm; }
  .fp-champ strong { font-size: 9pt; }
  .fp-phase-desc { padding: 2.5mm; line-height: 1.45; overflow-wrap: anywhere; }
  .fp-pieces { font-size: 8.5pt; color: var(--color-fg-muted); margin-left: 4mm !important; }
`;

/** Titre du document — celui que le navigateur propose comme nom de PDF. */
export function titreImpression(dossiers: Dossier[]): string {
  return dossiers.length === 1
    ? `${dossiers[0]!.sequence.title} - Fiche de préparation`
    : `Fiches de préparation - ${pluriel(dossiers.length, 'séquence')}`;
}

/** Le document des séquences choisies, prêt à être téléchargé en PDF. */
export function documentFiches(dossiers: Dossier[]): { titre: string; corps: string; style: string } {
  return {
    titre: titreImpression(dossiers),
    corps: dossiers.map(htmlSequence).join(''),
    style: STYLE_FICHES,
  };
}
